// Constantes e tipos compartilhados entre a tela de Contas (§5.4/§5.5) e os modais
// de lançamento (TxnModal). Extraídos pra permitir reuso do TxnModal na Visão de
// competência ("Nova ▾") e na ação global "Novo registro" (§5.11).

export type Kind = 'despesa' | 'receita'
// opens_split só vem preenchido na lista de CATEGORIAS (marca que o rateio abre
// sozinho ao escolher a categoria). Opcional porque Opt também serve centro de
// custo, conta e afins, que não têm essa noção.
export interface Opt { id: number; name: string; opens_split?: 0 | 1 }

// Uma parte do rateio, no formato do FORMULÁRIO (tudo string, porque vem de
// <input>). Morava dentro do TxnModal; subiu para cá em 2026-07-30 porque o
// RateioCategoriaBox devolve linhas neste formato ao aplicar um modelo ou
// copiar um rateio anterior.
export type SplitRow = {
  // Chave estável da linha. Sem ela o React usa o índice, e remover uma parte
  // faz o estado interno da linha (lista aberta, texto digitado) vazar para a
  // parte de baixo.
  uid?: string
  category_id: string; amount: string; description: string
  // Nome da categoria como veio da API. Só de exibição — o que é gravado é o
  // category_id. Existe porque o combobox resolvia o nome por lookup na lista
  // de categorias e, quando o lookup não casava, o campo aparecia vazio.
  category_name?: string
}

// Uma parte do MODELO de rateio da categoria (category_split_template).
// percent e amount são mutuamente exclusivos e os DOIS podem ser null — nesse
// caso a parte só fixa a categoria e o valor fica em branco pro usuário
// digitar. Ver o comentário da migração 038.
export interface TplItem {
  id: number
  target_category_id: number | null
  target_category_name: string | null
  cost_center_id: number | null
  cost_center_name: string | null
  percent: string | null
  amount: string | null
  description: string | null
  position: number
}

// Uma parte do rateio como a LISTA a recebe (valor já numérico, ao contrário do
// SplitRow do formulário). `id: null` é a sobra ainda não classificada, que a
// API acrescenta pra soma das partes fechar exato com o valor do lançamento.
// `matched` marca a parte que casou com a busca — é o que permite abrir a linha
// já expandida em cima do item procurado.
export interface SplitPart {
  id: number | null
  category_id: number | null
  category_name: string | null
  amount: number
  description: string | null
  matched: boolean
}

export interface Txn {
  id: number; description: string; party_name: string | null; party_doc?: string | null
  competence_date: string; due_date: string; payment_date: string | null
  amount: string; status: string; effective_status: 'vencido' | 'vence_hoje' | 'em_aberto' | 'agendado' | 'pago' | 'perda'
  category_id: number; cost_center_id: number | null; bank_account_id: number | null
  payment_method: string | null; reference_code: string | null; nsu: string | null
  recurrence_group_id: string | null; installment_no: number; installment_total: number; notes: string | null
  category_name: string | null; cost_center_name: string | null; account_name: string | null
  account_bank_code: string | null
  account_type: string | null
  // Rateio. Opcionais porque só a lista de Contas preenche — o TxnModal e a
  // Visão de competência consomem o mesmo tipo e não recebem estes campos.
  splits?: SplitPart[]
  split_count?: number
  split_total?: number
  split_match_count?: number
  split_match_amount?: number
}

// Normaliza pra comparação sem acento/caixa ("Água" ≡ "agua") — usada nos
// comboboxes de categoria (TxnModal e Favorecidos) e no match automático
// categoria-do-favorecido → categoria do lançamento.
export const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

// Lê valor digitado ou vindo da API. Precisa aceitar os dois formatos:
// a API manda "3195.86" (ponto decimal) e a pessoa digita "1.000,00" (ponto de
// milhar). A regra é a mesma que o "Colar do Excel" já usava: se tem vírgula,
// ponto é milhar; se não tem, ponto é decimal.
//
// Antes o código de gravação fazia só `replace(',', '.')`, então "1.000,00"
// virava "1.000.00" -> NaN -> 0, e o filtro de `amount > 0` DESCARTAVA a parte
// inteira do salvamento, sem aviso nenhum.
export const paraNumero = (v: string | number | null | undefined): number => {
  if (typeof v === 'number') return v
  let t = String(v ?? '').trim().replace(/[^\d.,-]/g, '')
  if (!t) return 0
  t = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t
  const n = Number(t)
  return isNaN(n) ? 0 : n
}

// Conta no campo de valor: "50,00+300+305,99" -> "655,99". Pedido do diretor
// (19/10/2026) para somar notas sem calculadora. Aceita + e -; cada termo passa
// pelo paraNumero, então "1.000,50" continua sendo mil. Sem operador devolve o
// texto como veio. Precisa rodar também no blur: o paraNumero sozinho apaga o
// "+" e "50+300" viraria 50300 ao salvar.
export const resolverConta = (v: string): string => {
  const t = String(v ?? '').replace(/\s/g, '')
  if (!/\d[+-]/.test(t)) return v
  const termos = t.match(/[+-]?[^+-]+/g) || []
  const total = termos.reduce((a, x) => a + (x[0] === '-' ? -paraNumero(x.slice(1)) : paraNumero(x.replace(/^\+/, ''))), 0)
  return (Math.round(total * 100) / 100).toFixed(2).replace('.', ',')
}

// Chave estável para linhas de rateio recém-criadas no formulário.
export const novoUid = () => Math.random().toString(36).slice(2, 10)

export const brl = (n: number | string) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
export const ptDate = (s: string | null) => (s ? s.split('-').reverse().join('/') : '—')

export const LABELS: Record<Kind, { newBtn: string; party: string; owed: string; paidCard: string; payTitle: string; noun: string }> = {
  despesa: { newBtn: 'Nova despesa', party: 'Fornecedor', owed: 'A pagar', paidCard: 'Pagos', payTitle: 'Pagar', noun: 'despesa' },
  receita: { newBtn: 'Nova receita', party: 'Cliente', owed: 'A receber', paidCard: 'Recebidos', payTitle: 'Receber', noun: 'receita' },
}

export const METHODS = [
  { v: 'pix', l: 'Pix' }, { v: 'boleto', l: 'Boleto' }, { v: 'cartao', l: 'Cartão' },
  { v: 'dinheiro', l: 'Dinheiro' }, { v: 'transferencia', l: 'Transferência' }, { v: 'outro', l: 'Outro' },
]

// Estilo do campo SEM largura. Use este quando o campo tiver largura própria
// (w-24, w-20…) ou for item de flex.
//
// Por que existe: `inputCls` começa com `w-full`, e escrever
// `${inputCls} w-24` NÃO funciona — no Tailwind quem vence não é a ordem no
// atributo class, é a ordem no CSS gerado, e `.w-full` vem depois de `.w-24`.
// Com `shrink-0` junto, o campo ficava com 100% da linha e se recusava a
// encolher: no rateio, o combobox de categoria ao lado era espremido para
// ZERO pixel e os botões saíam para fora do modal. A categoria estava lá o
// tempo todo, invisível. Medido com Playwright em 2026-07-30: combobox
// width=0px, campo de valor width=447px numa linha de 447px.
export const inputBase = 'rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

export const inputCls = `w-full ${inputBase}`
